/* eslint-disable react-hooks/exhaustive-deps */
import { Paging, defaultPagination } from '@Core';
import {
    CameraStatisticsByServiceType,
    CameraType,
    CameraUpdatedToday,
    IGetCameraProps,
    SortByField,
    SortByValue,
} from '@EcamModel/controllers/ICameraHttpController';
import { IGetLocationProps } from '@EcamModel/controllers/ILocationHttpController';
import {
    BatteryStatus,
    Camera,
    CameraPriority,
    CameraWithInfo,
    CaptureInfoUploadMode,
    ExternalBatteryPackMode,
    GeoZoneType,
    Location,
    PowerSupply,
    PurposeType,
    Status,
    WifiStatus,
} from '@EcamModel/model';
import { ViewPreset } from '@EcamModel/model/CameraView';
import { Zones } from '@EcamModel/model/Zones';
import BaseCheckbox from '@components/BaseCheckbox';
import BaseListRender from '@components/BaseListRender';
import StyledDateRangePicker from '@components/date-range-select/StyledDateRangePicker';
import FilterRoot from '@components/filter/FilterRoot';
import IMultiSelectWithCheckBox from '@components/multi-select/IMultiSelectWithCheckBox';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import BaseCustomTabs from '@components/tabs/BaseCustomTabs';
import { TabConfig } from '@components/tabs/BaseEnhancedTabs';
import { pushError, pushSuccess } from '@components/toast';
import { cameraController, locationController } from '@controllers/index';
import useScrollToTop from '@hooks/useScrollToTop';
import { SelectionItem } from '@models/cameras/SelectionItem';
import { MultiSearch } from '@models/search-model/SearchModel';
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { filteredAndSortedOptionsPriorities, locationAutocompleteStatus } from '@pages/locations';
import { getPriorityLocationsColor, getStatusLocationColor } from '@pages/map-overview/utils';
import { SelectWithType } from '@pages/overview/components/GraphReportsAnalyzingCriteriaAndPCN';
import { ReportsAnalyzingPcnWithData } from '@pages/overview/hook/ReportsAnalyzingCriteriaAndPcnProvider';
import { CustomTabPanel } from '@pages/overview/tabs/useTabsData';
import { ContraventionAuditEventSelection } from '@pages/verification/pc-details/components/recordedPCNEventLog/configs';
import color from '@theme/Colors';
import _, { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { filterUndefinedParams } from 'src/helpers';
import { LocationStatusReadableMapping } from '../details/components/CameraInformation';
import CameraItem from './components/CameraItem';
import SearchTypeSelect from './components/SearchTypeSelect';
import { search } from './helpers';

export interface FEFilterOptions {
    page?: number;
    pagePCNsByContraventionDate?: number;
    pageReasonCancellation?: number;
    pageSize?: number;
    search?: MultiSearch;
    location?: Location;
    zones?: Zones[];
    status?: SelectionItem;
    battery?: SelectionItem;
    connection?: SelectionItem;
    emailVerifier?: string;
    emailAuditor?: string;
    auditStatus?: BaseSelect;
    VerificationDateFrom?: Date;
    VerificationDateTo?: Date;
    sortedOrder?: BaseSelect;
    updated?: SelectionItem;
    from?: Date;
    to?: Date;
    sort?: BaseSelect;
}

export enum CameraSearchType {
    Name,
    Mac,
    IPAddress,
}

export type Filter = {
    page: number;
    search?: string;
    searchType?: BaseSelect;
    location?: Location;
    firmware?: BaseSelect;
    zones?: Zones[];
    status?: BaseSelect;
    battery?: BaseSelect;
    powerSupply?: BaseSelect;
    cameraPriority?: BaseSelect;
    connection?: BaseSelect;
    sort?: BaseSelect;
    sortedOrder?: BaseSelect;
    updated?: BaseSelect;
    from?: Date;
    to?: Date;
    fromStart?: Date;
    toStart?: Date;
    fromEnd?: Date;
    toEnd?: Date;
    camVersion?: BaseSelect;
    uploadMode?: BaseSelect;
    eventLog?: BaseSelect;
    entityName?: BaseSelect;
    verifierEventLog?: ContraventionAuditEventSelection;
    camPurposes?: BaseSelect;
    locationStatus?: BaseSelect;
    locationPriorities?: BaseSelect;
    taskPriorities?: BaseSelect;
    serviceType?: BaseSelect;
    ping?: BaseSelect;
    perspectiveView?: Partial<ViewPreset>;
    perspectiveDetailView?: Partial<ViewPreset>;
    reportAnalyzing?: SelectWithType<ReportsAnalyzingPcnWithData>;
    geozones?: { label?: string; id?: number; type?: GeoZoneType; hasView?: boolean };
    lensCapacity?: string;
    externalBattery?: BaseSelect;
    hasExtendedCoverage?: boolean;
    cameraType?: BaseSelect;
};

export const initPaging = {
    page: 1,
    pageSize: 10,
    rows: [],
    total: 0,
    totalPages: 0,
};

export type BaseSelect = {
    Id: number;
    Name: string;
    Value: any;
};

export const camerasSearchTypes: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Name',
        Value: CameraSearchType.Name,
    },
    {
        Id: 2,
        Name: 'MAC',
        Value: CameraSearchType.Mac,
    },
    {
        Id: 3,
        Name: 'IP Address',
        Value: CameraSearchType.IPAddress,
    },
];

const cameraSortByType: BaseSelect[] = [
    {
        Id: 4,
        Name: 'Camera Name',
        Value: SortByField.CameraName,
    },
    {
        Id: 1,
        Name: 'Zone',
        Value: SortByField.Zone,
    },
    {
        Id: 2,
        Name: 'Last Contact',
        Value: SortByField.Updated,
    },
    {
        Id: 3,
        Name: 'Battery Level',
        Value: SortByField.BatteryLevel,
    },
];

export const cameraSortByStatus: BaseSelect[] = [
    {
        Id: 1,
        Name: 'ASC',
        Value: SortByValue.Asc,
    },
    {
        Id: 2,
        Name: 'DESC',
        Value: SortByValue.Desc,
    },
];

const filterUpdated: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Not updated today',
        Value: CameraUpdatedToday.Updated,
    },
];

export const statuses: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Not available',
        Value: Status.NotAvailable,
    },
    {
        Id: 2,
        Name: 'Active',
        Value: Status.Active,
    },
    {
        Id: 3,
        Name: 'Inactive',
        Value: Status.Inactive,
    },
];
export const purposes: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Decommissioned',
        Value: PurposeType.Decommissioned,
    },
    {
        Id: 2,
        Name: 'Live',
        Value: PurposeType.Live,
    },
    {
        Id: 3,
        Name: 'On hold',
        Value: PurposeType.OnHold,
    },
    {
        Id: 4,
        Name: 'Testing',
        Value: PurposeType.Testing,
    },
    {
        Id: 5,
        Name: 'Prospective',
        Value: PurposeType.Prospective,
    },
    {
        Id: 6,
        Name: 'Deleted',
        Value: PurposeType.Deleted,
    },
];

const connections: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Connected',
        Value: WifiStatus.Connected,
    },
    {
        Id: 2,
        Name: 'Disconnected',
        Value: WifiStatus.DisConnected,
    },
];

const batteries: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Critical (0%-20%)',
        Value: BatteryStatus.Critical,
    },
    {
        Id: 2,
        Name: 'Low (21%-50%)',
        Value: BatteryStatus.Low,
    },
    {
        Id: 3,
        Name: 'Normal (51%-100%)',
        Value: BatteryStatus.Normal,
    },
];

export const powerSupplies: BaseSelect[] = [
    {
        Id: 1,
        Name: '45W/180Wh',
        Value: PowerSupply.Kit180Wh,
    },
    {
        Id: 2,
        Name: 'Hybrid',
        Value: PowerSupply.Hybrid,
    },
    {
        Id: 3,
        Name: '100W/555Wh',
        Value: PowerSupply.Kit555Wh,
    },
    {
        Id: 4,
        Name: '100W/962Wh',
        Value: PowerSupply.Kit962Wh,
    },
    {
        Id: 5,
        Name: 'Wired',
        Value: PowerSupply.Wired,
    },
    {
        Id: 6,
        Name: '1536Wh',
        Value: PowerSupply.Kit1536Wh,
    },
];

export const priorities: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Low',
        Value: CameraPriority.Low,
    },
    {
        Id: 2,
        Name: 'Medium',
        Value: CameraPriority.Medium,
    },
    {
        Id: 3,
        Name: 'High',
        Value: CameraPriority.High,
    },
];

const uploadMode: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Immediacy',
        Value: CaptureInfoUploadMode.IMMEDIACY,
    },
    {
        Id: 2,
        Name: 'Timing',
        Value: CaptureInfoUploadMode.TIMING,
    },
];

export const externalPowerBattery: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Internal battery',
        Value: ExternalBatteryPackMode.InternalBattery,
    },
    {
        Id: 2,
        Name: 'External power battery',
        Value: ExternalBatteryPackMode.ExternalPowerBattery,
    },
];

export const mappingCameraTypes = {
    [CameraType.PTZ]: 'Camera View360',
    [CameraType.Solar]: 'Camera Solar',
};

export const cameraTypes: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Solar',
        Value: CameraType.Solar,
    },
    {
        Id: 2,
        Name: 'View 360 (PTZ)',
        Value: CameraType.PTZ,
    },
    {
        Id: 3,
        Name: 'SP111',
        Value: CameraType.SP111,
    },
    {
        Id: 4,
        Name: 'PMC8266',
        Value: CameraType.PMC8266,
    },
];

const initLoading = { location: true, cameras: true };

type CountCamerasInLocation = {
    cameraPTZ?: number;
    cameraSolar?: number;
    cameraSP111?: number;
};

export default function Cameras() {
    const navigate = useNavigate();
    useScrollToTop();

    const [loadingState, setLoadingState] = useState<LoadingState>({ ...initLoading });
    const [pagingCameraSolar, setPagingCameraSolar] = useState<Paging<CameraWithInfo>>({ ...initPaging });
    const [pagingCameraPTZ, setPagingCameraPTZ] = useState<Paging<CameraWithInfo>>({ ...initPaging });
    const [pagingCameraSP111, setPagingCameraSP111] = useState<Paging<CameraWithInfo>>({ ...initPaging });
    const [pagingCameras, setPagingCameras] = useState<Paging<CameraWithInfo>>({ ...initPaging });

    const [zones, setZones] = useState<Zones[]>([]);
    const lensCapacityCacheRef = useRef<string[] | null>(null);

    const [countCamerasInLocation, setCountCamerasInLocation] = useState<CameraStatisticsByServiceType>({
        PTZCameraCount: 0,
        Solar4gCameraCount: 0,
        SP111CameraCount: 0,
        PMC8266CameraCount: 0,
    });

    const [searchParams, setSearchParams] = useSearchParams({});

    const [filter, setFilter] = useState<Filter>(() => ({
        page: 1,
        sortedOrder: cameraSortByStatus[0],
        sort: cameraSortByType[0],
        searchType: camerasSearchTypes[0],
        cameraType: cameraTypes.find((c) => c.Id === Number(searchParams.get('cameraType'))),
    }));
    const [searchValue, setSearchValue] = useState('');
    const [isFilterReady, setIsFilterReady] = useState(false);

    const { search: searchUseLocation } = useLocation();
    const queryParams = new URLSearchParams(searchUseLocation);
    const locationIdNavigate = decodeURIComponent(queryParams.get('locationId') || '');
    console.log('🚀 ~ Cameras ~ locationIdNavigate:', locationIdNavigate);

    const setPartialLoading = (loadings: Partial<LoadingState>) => {
        setLoadingState((prev) => ({ ...prev, ...loadings }));
    };

    const statusAutocomplete = useStyledAutocomplete({
        list: {
            options: statuses,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const connectionsAutocomplete = useStyledAutocomplete({
        list: {
            options: connections,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const batteryAutocomplete = useStyledAutocomplete({
        list: {
            options: batteries,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const powerSuppliesAutocomplete = useStyledAutocomplete({
        list: {
            options: powerSupplies,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const cameraPrioritiesAutocomplete = useStyledAutocomplete({
        list: {
            options: priorities,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const updatedAutocomplete = useStyledAutocomplete({
        list: {
            options: filterUpdated,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const uploadModeAutocomplete = useStyledAutocomplete({
        list: {
            options: uploadMode,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const externalBatteryAutocomplete = useStyledAutocomplete({
        list: {
            options: externalPowerBattery,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const purposesAutocomplete = useStyledAutocomplete({
        list: {
            options: purposes,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const lensCapacityAutocomplete = useStyledAutocomplete({
        async getList(props): Promise<Paging<string>> {
            if (!lensCapacityCacheRef.current) {
                const res = await cameraController.getLensCapacity();
                lensCapacityCacheRef.current = res;
            }

            let filteredData = lensCapacityCacheRef.current;
            const searchText = props?.search?.content || '';
            if (searchText) {
                filteredData = filteredData.filter((item) => search(item, searchText));
            }

            return { ...defaultPagination(), rows: filteredData };
        },
    });

    const locationAutocomplete = useStyledAutocomplete({
        async getList(props: IGetLocationProps): Promise<Paging<any>> {
            const res = await locationController.listHasCamera(props).finally(() => setIsFilterReady(true));
            const _location = res.rows?.find((l) => l.Id === parseInt(locationId!));
            console.log('🚀 ~ getList ~ _location:', _location);
            const convertedZones = zoneIds?.split(',').map(Number);
            const _zones = _location?.Zones?.filter((z) => convertedZones?.includes(z.Id!));
            const initialZone = zones.length > 0 ? _zones : _location?.Zones || [];
            setZones(initialZone || []);

            const shouldUseDefaultStatus = isDefaultFilter !== '1';

            const _filter: Partial<Filter> = {
                locationStatus: _locationStatus || undefined,
                locationPriorities: _locationPriority || undefined,
                zones: _zones && _zones.length > 0 ? _zones : undefined,
                page: page ? Number(page) : 1,
                updated: _lastContact || undefined,
                from: from ? moment(from).startOf('d').toDate() : undefined,
                to: to ? moment(to).endOf('d').toDate() : undefined,
                status: _status || (shouldUseDefaultStatus ? statuses[1] : undefined),
                connection: _connection || undefined,
                battery: _battery || undefined,
                sortedOrder: _sortType || cameraSortByStatus[0],
                sort: _sortBy || cameraSortByType[0],
                searchType: _searchType || camerasSearchTypes[0],
                uploadMode: _uploadMode || undefined,
                externalBattery: _externalBattery || undefined,
                lensCapacity: lensCapacity || undefined,
                camPurposes: _purposes || undefined,
                powerSupply: _powerSupply || undefined,
                cameraPriority: _cameraPriority || undefined,
                hasExtendedCoverage: hasExtendedCoverage === 'true' ? true : undefined,
            };

            if (!from) delete _filter.from;
            if (!to) delete _filter.to;

            setFilter((prev) => ({
                ...prev,
                ..._filter,
            }));

            handleChangeParams({
                ...searchParams,
                page,
                status: _status?.Id || (shouldUseDefaultStatus ? statuses[1].Id : undefined),
                cameraType: filter.cameraType?.Id,
                isDefaultFilter: shouldUseDefaultStatus ? '0' : '1',
            });

            return res;
        },
        listProps: {
            search: { fields: ['name'] } as any,
            filter: {
                LocationStatus: filter.locationStatus?.Value ?? undefined,
                Priority: filter.locationPriorities?.Value ?? undefined,
            },
            IncludeECStats: filter.cameraType?.Value === CameraType.PTZ ? true : undefined,
        },
        dependencyList: [filter.locationStatus, filter.locationPriorities, filter.cameraType?.Value],
    });

    const locationStatusAutocomplete = useStyledAutocomplete({
        list: {
            options: locationAutocompleteStatus,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const locationPrioritiesStatusAutocomplete = useStyledAutocomplete({
        list: {
            options: filteredAndSortedOptionsPriorities,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const page = searchParams.get('page');

    const cameraType = Number(searchParams.get('cameraType'));

    const searchBy = searchParams.get('searchBy');
    const searchType = searchParams.get('searchType');
    const _searchType = camerasSearchTypes.find((s) => s.Id === Number(searchType)) || camerasSearchTypes[0];

    const sortType = searchParams.get('sortType');
    const _sortType = cameraSortByStatus.find((c) => c.Id === Number(sortType));

    const sortBy = searchParams.get('sortBy');
    const _sortBy = cameraSortByType.find((c) => c.Id === Number(sortBy));

    const locationId = searchParams.get('locationId');

    const locationStatus = searchParams.get('locationStatus');
    const _locationStatus = locationAutocompleteStatus.find((u) => u.Id === Number(locationStatus));

    const locationPriorities = searchParams.get('locationPriorities');
    const _locationPriority = filteredAndSortedOptionsPriorities.find((u) => u.Id === Number(locationPriorities));

    const zoneIds = searchParams.get('zoneIds');

    const lastContact = searchParams.get('lastContact');
    const _lastContact = filterUpdated.find((u) => u.Id === Number(lastContact));

    const from = searchParams.get('from');
    const to = searchParams.get('to');

    const status = searchParams.get('status');
    const _status = statuses.find((s) => s.Id === Number(status));

    const connection = searchParams.get('connection');
    const _connection = connections.find((s) => s.Id === Number(connection));

    const battery = searchParams.get('battery');
    const _battery = batteries.find((s) => s.Id === Number(battery));

    const powerSupply = searchParams.get('powerSupply');
    const _powerSupply = powerSupplies.find((ps) => ps.Id === Number(powerSupply));

    const cameraPriority = searchParams.get('cameraPriority');
    const _cameraPriority = priorities.find((ps) => ps.Id === Number(cameraPriority));

    const mode = searchParams.get('mode');
    const _uploadMode = uploadMode.find((s) => s.Id === Number(mode));

    const externalBattery = searchParams.get('externalBattery');
    const _externalBattery = externalPowerBattery.find((s) => s.Id === Number(externalBattery));

    const purposesCam = searchParams.get('purposesCam');
    const _purposes = purposes.find((s) => s.Id === Number(purposesCam));

    const isDefaultFilter = searchParams.get('isDefaultFilter');
    const lensCapacity = searchParams.get('lensCapacity');
    const hasExtendedCoverage = searchParams.get('hasExtendedCoverage');

    const handleChangeParams = (params: any) => {
        const _searchParams: any = {
            page,
            searchType,
            searchBy,
            sortType,
            sortBy,
            locationId,
            locationStatus,
            locationPriorities,
            zoneIds,
            lastContact,
            from,
            to,
            status,
            connection,
            battery,
            powerSupply,
            cameraPriority,
            mode,
            externalBattery,
            purposesCam,
            lensCapacity,
            hasExtendedCoverage,
            cameraType,
            isDefaultFilter,
            ...params,
        };

        if (!('page' in params)) {
            delete _searchParams.page;
        }

        Object.entries(_searchParams).forEach(([key, value]) => {
            const firstPage = key === 'page' && Number(value) === 1;
            // if (!_searchParams['locationId']) delete _searchParams['zoneIds'];
            if (!value || firstPage) delete _searchParams[key];
        });

        setSearchParams(new URLSearchParams(_searchParams));
    };

    const resetSearchParams = (newValue: number) => {
        setSearchValue(searchType === '2' ? searchValue : '');

        const newSearchParams = filterUndefinedParams({
            ...searchParams,
            status: statuses[1].Id.toString(),
            cameraType: newValue.toString(),
            searchBy: searchType === '2' ? searchBy?.toString() : undefined,
            searchType: searchType === '2' ? camerasSearchTypes[1] : undefined,
            hasExtendedCoverage: undefined,
        });

        const currentSortBy = sortBy ? Number(sortBy) : null;
        if (currentSortBy && (newValue === CameraType.Solar || (currentSortBy !== 2 && currentSortBy !== 3))) {
            newSearchParams.sortBy = sortBy;
        }

        setSearchParams(new URLSearchParams(newSearchParams));

        setFilter((prev) => ({
            ...prev,
            page: 1,
            sort: currentSortBy === 2 || currentSortBy === 3 ? cameraSortByType[0] : prev.sort,
            updated: undefined,
            connection: undefined,
            battery: undefined,
            powerSupply: undefined,
            cameraPriority: undefined,
            uploadMode: undefined,
            searchType: searchType === '2' ? camerasSearchTypes[1] : camerasSearchTypes[0],
        }));
    };

    const debSearchClaim = useCallback(
        debounce((nextValue) => {
            setFilter((prev) => ({ ...prev, search: nextValue, page: 1 }));
        }, 500),
        []
    );
    const handleDebSearchClaim = (e: any) => {
        const value = e.target.value;
        setSearchValue(value);
        debSearchClaim(value);
    };

    const getCameras = useCallback(
        async (props?: Partial<IGetCameraProps>) => {
            setPartialLoading({ cameras: true });

            const isAllCameraTypes = filter.cameraType?.Value === undefined || filter.cameraType?.Value === null;
            const isSolarType = filter.cameraType?.Value === CameraType.Solar;
            const isMacSearch = !isAllCameraTypes && _searchType?.Value === CameraSearchType.Mac;

            const searchFields = (
                _searchType?.Value === CameraSearchType.Name
                    ? ['Name']
                    : _searchType?.Value === CameraSearchType.IPAddress
                      ? ['IP']
                      : []
            ) as (keyof Camera)[];

            const requestedSortByField = _sortBy?.Value ?? SortByField.CameraName;
            const sortByField =
                !isSolarType &&
                (requestedSortByField === SortByField.BatteryLevel || requestedSortByField === SortByField.Updated)
                    ? SortByField.CameraName
                    : requestedSortByField;

            const hasDateRange = !!from && !!to;

            const _filter = {
                page: Number(page) || 1,
                LocationId: Number(locationId) || undefined,
                // LocationStatus: _locationStatus?.Value ?? undefined,
                // LocationPriority: _locationPriority?.Value ?? undefined,
                ZoneIds: !!zoneIds ? zoneIds.split(',').map(Number) : undefined,
                NotUpdatedToday: Number(lastContact) || undefined,
                pageSize: 10,
                From: hasDateRange ? moment(from).startOf('d').toDate() : undefined,
                To: hasDateRange ? moment(to).endOf('d').toDate() : undefined,
                Battery: _battery?.Value,
                Wifi: _connection?.Value,
                filter: {
                    Status: _status?.Value,
                    PurposeType: _purposes?.Value,
                    PowerSupply: _powerSupply?.Value,
                    Priority: _cameraPriority?.Value,
                    LensCapacity: lensCapacity || undefined,
                },
                SortByField: sortByField,
                SortByValue: _sortType?.Value ?? SortByValue.Asc,
                search: {
                    fields: searchFields,
                    content: isMacSearch ? '' : searchBy!,
                },
                Mac: isMacSearch ? searchBy! : undefined,
                UploadMode: _uploadMode?.Value,
                CameraType: filter.cameraType?.Value,
                HasExtendedCoverage:
                    filter.cameraType?.Value === CameraType.PTZ
                        ? hasExtendedCoverage === 'true' || undefined
                        : undefined,
                ..._.omit(props),
            };

            cameraController
                .listAllCameras(_filter as any)
                .then((res) => {
                    setPagingCameras(res);
                })
                .finally(() => setPartialLoading({ cameras: false }));
        },
        [
            page,
            locationId,
            locationStatus,
            locationPriorities,
            zoneIds,
            lastContact,
            from,
            to,
            battery,
            powerSupply,
            cameraPriority,
            _status,
            _purposes,
            _powerSupply,
            _cameraPriority,
            _connection,
            _battery,
            _uploadMode,
            lensCapacity,
            _sortBy,
            _sortType,
            _searchType,
            searchBy,
            filter.cameraType,
            hasExtendedCoverage,
        ]
    );

    const handleResetFilter = () => {
        setSearchValue('');
        setFilter({
            sortedOrder: cameraSortByStatus[0],
            sort: cameraSortByType[0],
            searchType: camerasSearchTypes[0],
            status: statuses[1],
        } as Filter);

        const newParams = {
            status: statuses[1].Id.toString(),
            isDefaultFilter: '0',
        };
        setSearchParams(newParams);
    };

    const onChangeStatus = (id: number, checked: boolean) => {
        const paging = { ...pagingCameras };
        paging.rows.forEach((c) => {
            if (c.Id === id) {
                c.Status = checked ? Status.Active : Status.Inactive;
            }
        });
        setPagingCameras({ ...paging });
    };

    const onChangeVerification = (cameraId: number, checked: boolean) => {
        const paging = { ...pagingCameras };
        paging.rows.forEach((c) => {
            if (c.Id === cameraId) {
                c.IsVerificationsEnabled = checked;
            }
        });
        setPagingCameras({ ...paging });
    };

    const onDeleteCamera = (id: number) => {
        cameraController.delete(id.toString()).then((res) => {
            if (filter.page === 1) {
                setPagingCameras((prev) => ({
                    ...prev,
                    rows: [...prev.rows?.filter((item) => item.Id !== id)],
                    total: prev.total - 1,
                }));
            }
            setFilter((prev) => ({ ...prev, page: 1 }));
            handleChangeParams({ page: 1 });
            pushSuccess('Delete camera successful');
        });
    };

    const onChangeLocation = (l?: Location) => {
        setFilter((prev) => ({ ...prev, location: l, zones: undefined, page: 1 }));
        setZones(l ? l.Zones! : []);
    };

    useEffect(() => {
        if (!locationId) return;
        const parsedLocationId = Number(locationId);
        if (Number.isNaN(parsedLocationId)) return;
        const matchedLocation = locationAutocomplete?.paging?.rows?.find((l) => l.Id === parsedLocationId);
        if (!matchedLocation) return;
        if (filter.location?.Id === matchedLocation.Id) return;

        setFilter((prev) => ({
            ...prev,
            location: matchedLocation,
        }));
    }, [locationId, locationAutocomplete.paging.rows]);

    useEffect(() => {
        if (!isFilterReady) return;
        getCameras({});
    }, [
        isFilterReady,
        filter.page,
        filter.search,
        filter.sort,
        filter.sortedOrder,
        filter.location,
        filter.locationStatus,
        filter.locationPriorities,
        filter.zones,
        filter.updated,
        filter.from,
        filter.to,
        filter.status,
        filter.connection,
        filter.battery,
        filter.powerSupply,
        filter.cameraPriority,
        filter.uploadMode,
        filter.externalBattery,
        filter.lensCapacity,
        filter.camPurposes,
        filter.hasExtendedCoverage,
        filter.cameraType,
    ]);

    useEffect(() => {
        if (filter.search) {
            getCameras({});
        }
    }, [filter.searchType]);

    useEffect(() => {
        setSearchValue(searchBy || '');
        handleChangeParams({
            ...searchParams,
            page,
            status: _status?.Id || statuses[1].Id,
            cameraType: filter.cameraType?.Id?.toString(),
        });
    }, []);

    const tabs: TabConfig[] = [
        {
            index: 0,
            label: `Camera Solar (${countCamerasInLocation.Solar4gCameraCount})`,
        },
        {
            index: 1,
            label: `Camera View360 (${countCamerasInLocation.PTZCameraCount})`,
        },
        {
            index: 2,
            label: `Camera SP111 (${countCamerasInLocation.SP111CameraCount})`,
        },
        {
            index: 3,
            label: `Camera PMC8266 (${countCamerasInLocation.PMC8266CameraCount})`,
        },
    ];

    const filteredCameraSortByType =
        filter.cameraType?.Value === CameraType.Solar
            ? cameraSortByType
            : cameraSortByType.filter((item) => item.Id !== 2 && item.Id !== 3);

    const getCountCameras = async () => {
        await cameraController
            .getCameraStatisticsByServiceType()
            .then((res) => {
                setCountCamerasInLocation({
                    PTZCameraCount: res.PTZCameraCount,
                    Solar4gCameraCount: res.Solar4gCameraCount,
                    SP111CameraCount: res.SP111CameraCount,
                    PMC8266CameraCount: res.PMC8266CameraCount,
                });
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            });
    };

    const renderLocationOption = (option: Location) => {
        const { colorText, bgColor } = getStatusLocationColor(option.LocationStatus!);
        const { bgColorPriority } = getPriorityLocationsColor(option?.Priority);
        const ecStats = option.ECStats;
        const hasECStats = filter.cameraType?.Value === CameraType.PTZ && ecStats && ecStats.CameraCount > 0;

        const content = (
            <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{ minWidth: 0, flexWrap: 'nowrap' }}>
                {option?.Priority !== null && (
                    <Typography
                        padding={'4px 8px'}
                        variant="body2"
                        borderRadius={'5px'}
                        sx={{
                            cursor: 'default',
                            color: color.textSecondary,
                            bgcolor: bgColorPriority,
                            fontSize: 12,
                            flexShrink: 0,
                        }}
                    >
                        {`P${option?.Priority}`}
                    </Typography>
                )}
                <Typography
                    variant="body1"
                    noWrap
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                    }}
                >
                    {option.Name}
                </Typography>
                <Typography
                    variant="body1"
                    color={colorText}
                    sx={{
                        padding: '4px 8px',
                        fontSize: 12,
                        background: bgColor,
                        borderRadius: '5px',
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {LocationStatusReadableMapping[option.LocationStatus!] ?? 'N/A'}
                </Typography>
                {hasECStats && (
                    <Typography noWrap sx={{ fontSize: 11, color: color.grey600, pl: 0.5 }}>
                        Coverage:{' '}
                        {`${ecStats.CameraCount} Camera${ecStats.CameraCount > 1 ? 's' : ''} - ${ecStats.LocationCount} Location${ecStats.LocationCount > 1 ? 's' : ''} - ${ecStats.ZoneCount} Zone${ecStats.ZoneCount > 1 ? 's' : ''}`}
                    </Typography>
                )}
            </Stack>
        );

        if (!hasECStats) return content;

        return (
            <Tooltip
                placement="right"
                componentsProps={{
                    tooltip: {
                        sx: {
                            maxWidth: 320,
                        },
                    },
                }}
                title={
                    <Stack spacing={1.5}>
                        {ecStats.Cameras.map((cam, idx) => (
                            <Stack key={idx} spacing={0.5}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
                                    Camera: {cam.CameraName}
                                </Typography>
                                {cam.Locations.map((loc, i) => (
                                    <Box key={i} sx={{ pl: 1 }}>
                                        <Typography sx={{ fontSize: 11, color: '#ccc' }}>
                                            Location: {`- ${loc.Name}`}
                                        </Typography>
                                        {loc.Zones.length > 0 &&
                                            loc.Zones.map((zone, j) => (
                                                <Typography key={j} sx={{ fontSize: 11, color: '#fff', pl: 2 }}>
                                                    {`• ${zone}`}
                                                </Typography>
                                            ))}
                                    </Box>
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                }
            >
                <Box sx={{ width: '100%' }}>{content}</Box>
            </Tooltip>
        );
    };

    useEffect(() => {
        getCountCameras();
    }, []);

    return (
        <Stack direction={'column'} width={'100%'}>
            <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} style={{ marginTop: '10px' }}>
                <Typography variant="h3">Camera list</Typography>
                <Button
                    variant="contained"
                    sx={{ maxWidth: '150px' }}
                    onClick={() => navigate(`add/${filter.cameraType?.Value ?? CameraType.Solar}`)}
                >
                    Add camera
                </Button>
            </Stack>

            <Box mt={2}>
                <FilterRoot
                    isCollapse
                    defaultFilterChildVisible={false}
                    baseStyle={{
                        borderRadius: '0px 10px 10px 10px',
                    }}
                    handleResetFilter={() => handleResetFilter()}
                    rightTitleContent={
                        <Grid2 container spacing={{ xs: 0, md: 2 }} rowSpacing={{ xs: 2, md: 0 }} xs={12} md={8}>
                            <Grid2 xs={12} md={4}>
                                <StyledAutocomplete
                                    {...useStyledAutocomplete({
                                        list: {
                                            options: cameraTypes,
                                            isFiltered: (option, searchText) => search(option.Name, searchText),
                                        },
                                    })}
                                    getOptionLabel={(o) => o.Name}
                                    label="Camera Type"
                                    value={filter.cameraType}
                                    placeholder="All camera types"
                                    disabled={loadingState.cameras}
                                    onChange={(e) => {
                                        const resetMacSearch =
                                            !e && filter.searchType?.Value === CameraSearchType.Mac;
                                        if (resetMacSearch) setSearchValue('');
                                        // Reset filters and sort when camera type changes
                                        setFilter((prev) => ({
                                            ...prev,
                                            cameraType: e,
                                            page: 1,
                                            sort: cameraSortByType[0],
                                            sortedOrder: cameraSortByStatus[0],
                                            searchType: resetMacSearch ? camerasSearchTypes[0] : prev.searchType,
                                            search: resetMacSearch ? undefined : prev.search,
                                            connection: undefined,
                                            updated: undefined,
                                            battery: undefined,
                                            powerSupply: undefined,
                                            cameraPriority: undefined,
                                            uploadMode: undefined,
                                            hasExtendedCoverage: undefined,
                                        }));
                                        handleChangeParams({
                                            cameraType: e?.Id,
                                            sortBy: cameraSortByType[0].Id,
                                            sortType: cameraSortByStatus[0].Id,
                                            searchType: resetMacSearch ? camerasSearchTypes[0].Id : searchType,
                                            searchBy: resetMacSearch ? undefined : searchBy,
                                            connection: undefined,
                                            lastContact: undefined,
                                            battery: undefined,
                                            powerSupply: undefined,
                                            cameraPriority: undefined,
                                            mode: undefined,
                                            hasExtendedCoverage: undefined,
                                        });
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={4}>
                                <TextField
                                    sx={{ '& .MuiOutlinedInput-root': { paddingLeft: 0 } }}
                                    fullWidth
                                    label="Search by"
                                    placeholder="Search..."
                                    onChange={(e) => {
                                        handleDebSearchClaim(e);
                                        handleChangeParams({
                                            searchBy: e.target.value,
                                            cameraType: filter.cameraType?.Id,
                                        });
                                        if (_searchType?.Value === CameraSearchType.Mac) {
                                            onChangeLocation(undefined);
                                            handleChangeParams({
                                                locationId: undefined,
                                                zoneIds: undefined,
                                                searchBy: e.target.value,
                                                cameraType: filter.cameraType?.Id,
                                            });
                                        }
                                    }}
                                    value={searchValue}
                                    InputProps={{
                                        startAdornment: (
                                            <Stack direction="row">
                                                <SearchTypeSelect
                                                    label="Search by"
                                                    minWidth={80}
                                                    listData={
                                                        filter.cameraType
                                                            ? camerasSearchTypes
                                                            : camerasSearchTypes.filter(
                                                                  (s) => s.Value !== CameraSearchType.Mac
                                                              )
                                                    }
                                                    value={filter.searchType!}
                                                    disabledLoading={loadingState.cameras}
                                                    keyEqual="Id"
                                                    keyLabel="Name"
                                                    handleChange={(e) => {
                                                        setFilter((prev) => ({ ...prev, searchType: e }));
                                                        handleChangeParams({
                                                            searchType: e.Id,
                                                            cameraType: filter.cameraType?.Id,
                                                        });
                                                    }}
                                                />
                                                <Divider
                                                    orientation="vertical"
                                                    flexItem
                                                    sx={{
                                                        mx: '5px',
                                                        borderRightWidth: 1,
                                                        borderColor: color.grey600,
                                                    }}
                                                    variant="middle"
                                                />
                                            </Stack>
                                        ),
                                        endAdornment: (
                                            <FiSearch
                                                color="disabled"
                                                size={22}
                                                style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                                            />
                                        ),
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={4}>
                                <SearchTypeSelect
                                    startAdornment={
                                        <Stack direction={'row'}>
                                            <SearchTypeSelect
                                                listData={cameraSortByStatus}
                                                value={filter.sortedOrder!}
                                                keyEqual="Id"
                                                keyLabel="Name"
                                                disabledLoading={loadingState.cameras}
                                                handleChange={(e) => {
                                                    setFilter((prev) => ({ ...prev, sortedOrder: e, page: 1 }));
                                                    handleChangeParams({
                                                        sortType: e?.Id,
                                                        cameraType: filter.cameraType?.Id,
                                                    });
                                                }}
                                                label="Sort by"
                                                minWidth={60}
                                            />
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                                sx={{
                                                    mx: '5px',
                                                    borderRightWidth: 1,
                                                    borderColor: color.grey600,
                                                }}
                                                variant="middle"
                                            />
                                        </Stack>
                                    }
                                    disabledLoading={loadingState.cameras}
                                    listData={filteredCameraSortByType}
                                    value={filter.sort!}
                                    keyEqual="Id"
                                    keyLabel="Name"
                                    handleChange={(e) => {
                                        setFilter((prev) => ({ ...prev, sort: e, page: 1 }));
                                        handleChangeParams({ sortBy: e?.Id, cameraType: filter.cameraType?.Id });
                                    }}
                                    label="Sort by"
                                    minWidth={60}
                                />
                            </Grid2>
                        </Grid2>
                    }
                    filterChild={
                        <Grid container spacing={1}>
                            {/* Location & Zone Group */}
                            <Grid item xs={12} md={filter.cameraType?.Value === CameraType.PTZ ? 4 : 3}>
                                <Stack spacing={2} bgcolor={color.white} p={2} borderRadius={2}>
                                    <Typography variant="h6">LOCATION & ZONE</Typography>
                                    <StyledAutocomplete
                                        {...locationAutocomplete}
                                        getOptionLabel={(o) => o.Name || ''}
                                        customRenderOption={(_, option) => renderLocationOption(option)}
                                        customRenderSelectedValue={(option) => renderLocationOption(option)}
                                        label="Location"
                                        value={filter.location}
                                        placeholder="All locations"
                                        disabled={loadingState.cameras}
                                        onChange={(l) => {
                                            onChangeLocation(l);
                                            handleChangeParams({ locationId: l?.Id, zoneIds: undefined });
                                        }}
                                    />
                                    <StyledAutocomplete
                                        {...locationStatusAutocomplete}
                                        getOptionLabel={(o) => o.Name}
                                        label="Location status"
                                        value={filter.locationStatus}
                                        placeholder="All location status"
                                        disabled={loadingState.cameras}
                                        onChange={(l) => {
                                            setFilter((prev) => ({ ...prev, locationStatus: l, page: 1 }));
                                            onChangeLocation(undefined);
                                            handleChangeParams({
                                                locationId: undefined,
                                                zoneIds: undefined,
                                                locationStatus: l?.Id,
                                            });
                                        }}
                                    />
                                    <StyledAutocomplete
                                        {...locationPrioritiesStatusAutocomplete}
                                        getOptionLabel={(o) => o.Name}
                                        label="Location priority"
                                        value={filter.locationPriorities}
                                        placeholder="All location priorities"
                                        disabled={loadingState.cameras}
                                        onChange={(p) => {
                                            setFilter((prev) => ({ ...prev, locationPriorities: p, page: 1 }));
                                            onChangeLocation(undefined);
                                            handleChangeParams({
                                                locationId: undefined,
                                                zoneIds: undefined,
                                                locationPriorities: p?.Id,
                                            });
                                        }}
                                    />
                                    <IMultiSelectWithCheckBox
                                        limitTags={3}
                                        label={'Zone'}
                                        keyId={'Id'}
                                        keyLabel={'PublicName'}
                                        placeHolder={'Select zone'}
                                        listData={zones}
                                        isDisable={
                                            !zones?.length || filter.location === undefined || loadingState.cameras
                                        }
                                        value={filter.zones ?? []}
                                        setFilter={(option) => {
                                            setFilter((prev) => ({
                                                ...prev,
                                                zones: option.length ? option : undefined,
                                                page: 1,
                                            }));
                                            const handlerIdsParam = option
                                                .map((item) => encodeURIComponent(item.Id!))
                                                .join(',');
                                            handleChangeParams({
                                                zoneIds: handlerIdsParam,
                                                cameraType: filter.cameraType?.Id,
                                            });
                                        }}
                                        chipSize={150}
                                    />
                                </Stack>
                            </Grid>

                            {/* Status Group */}
                            <Grid item xs={12} md={3}>
                                <Stack spacing={2} bgcolor={color.white} p={2} borderRadius={2}>
                                    <Typography variant="h6">STATUS</Typography>
                                    <StyledAutocomplete
                                        {...statusAutocomplete}
                                        getOptionLabel={(o) => o.Name}
                                        label="Status"
                                        value={filter.status}
                                        placeholder="All status"
                                        disabled={loadingState.cameras}
                                        onChange={(o) => {
                                            setFilter((prev) => ({ ...prev, status: o, page: 1 }));
                                            handleChangeParams({ status: o?.Id, cameraType: filter.cameraType?.Id });
                                        }}
                                    />
                                    {filter.cameraType?.Value === CameraType.PTZ && (
                                        <FormControlLabel
                                            control={
                                                <BaseCheckbox
                                                    checked={filter.hasExtendedCoverage ?? false}
                                                    disabled={loadingState.cameras}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setFilter((prev) => ({
                                                            ...prev,
                                                            hasExtendedCoverage: checked,
                                                            page: 1,
                                                        }));
                                                        handleChangeParams({
                                                            hasExtendedCoverage: checked ? 'true' : undefined,
                                                            cameraType: filter.cameraType?.Id,
                                                        });
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography ml={0.5} mt={0.3}>
                                                    Has Extended Coverage
                                                </Typography>
                                            }
                                            sx={{ ml: 0 }}
                                        />
                                    )}
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...connectionsAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="Connection"
                                            value={filter.connection}
                                            placeholder="All connections"
                                            disabled={loadingState.cameras}
                                            onChange={(o) => {
                                                setFilter((prev) => ({ ...prev, connection: o, page: 1 }));
                                                handleChangeParams({
                                                    connection: o?.Id,
                                                    cameraType: filter.cameraType?.Id,
                                                });
                                            }}
                                        />
                                    )}
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...updatedAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="Last contact"
                                            value={filter.updated}
                                            placeholder="None"
                                            disabled={loadingState.cameras}
                                            onChange={(e) => {
                                                setFilter((prev) => ({ ...prev, updated: e, page: 1 }));
                                                handleChangeParams({
                                                    lastContact: e?.Id,
                                                    cameraType: filter.cameraType?.Id,
                                                });
                                            }}
                                        />
                                    )}
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...batteryAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="Battery"
                                            value={filter.battery}
                                            placeholder="All levels"
                                            disabled={loadingState.cameras}
                                            onChange={(o) => {
                                                setFilter((prev) => ({ ...prev, battery: o, page: 1 }));
                                                handleChangeParams({
                                                    battery: o?.Id,
                                                    cameraType: filter.cameraType?.Id,
                                                });
                                            }}
                                        />
                                    )}
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...powerSuppliesAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="Power supply"
                                            value={filter.powerSupply}
                                            placeholder="All power supplies"
                                            disabled={loadingState.cameras}
                                            onChange={(o) => {
                                                setFilter((prev) => ({ ...prev, powerSupply: o, page: 1 }));
                                                handleChangeParams({
                                                    powerSupply: o?.Id,
                                                    cameraType: filter.cameraType?.Id,
                                                });
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Grid>

                            {/* Camera Specs Group */}
                            <Grid item xs={12} md={3}>
                                <Stack spacing={2} bgcolor={color.white} p={2} borderRadius={2}>
                                    <Typography variant="h6">CAMERA SPECS</Typography>
                                    <StyledAutocomplete
                                        {...lensCapacityAutocomplete}
                                        getOptionLabel={(o) => o}
                                        label="Lens capacity"
                                        value={filter.lensCapacity}
                                        placeholder="All lens capacities"
                                        disabled={loadingState.cameras}
                                        onChange={(o) => {
                                            setFilter((prev) => ({ ...prev, lensCapacity: o, page: 1 }));
                                            handleChangeParams({ lensCapacity: o, cameraType: filter.cameraType?.Id });
                                        }}
                                    />
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...cameraPrioritiesAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="Camera priority"
                                            value={filter.cameraPriority}
                                            placeholder="All camera priorities"
                                            disabled={loadingState.cameras}
                                            onChange={(o) => {
                                                setFilter((prev) => ({ ...prev, cameraPriority: o, page: 1 }));
                                                handleChangeParams({
                                                    cameraPriority: o?.Id,
                                                    cameraType: filter.cameraType?.Id,
                                                });
                                            }}
                                        />
                                    )}
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...uploadModeAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="Upload mode"
                                            value={filter.uploadMode}
                                            placeholder="All mode"
                                            disabled={loadingState.cameras}
                                            onChange={(o) => {
                                                setFilter((prev) => ({ ...prev, uploadMode: o, page: 1 }));
                                                handleChangeParams({ mode: o?.Id, cameraType: filter.cameraType?.Id });
                                            }}
                                        />
                                    )}
                                    {filter.cameraType?.Value === CameraType.Solar && (
                                        <StyledAutocomplete
                                            {...externalBatteryAutocomplete}
                                            getOptionLabel={(o) => o.Name}
                                            label="External battery pack"
                                            value={filter.externalBattery}
                                            disabled={loadingState.cameras}
                                            onChange={(e) => {
                                                setFilter((prev) => ({ ...prev, externalBattery: e, page: 1 }));
                                                handleChangeParams({
                                                    externalBattery: e?.Id,
                                                    cameraType: filter.cameraType?.Id,
                                                });
                                            }}
                                        />
                                    )}
                                    <StyledAutocomplete
                                        {...purposesAutocomplete}
                                        getOptionLabel={(o) => o.Name}
                                        label="Purposes"
                                        value={filter.camPurposes}
                                        placeholder="All purposes"
                                        disabled={loadingState.cameras}
                                        onChange={(o) => {
                                            setFilter((prev) => ({ ...prev, camPurposes: o, page: 1 }));
                                            handleChangeParams({
                                                purposesCam: o?.Id,
                                                cameraType: filter.cameraType?.Id,
                                            });
                                        }}
                                    />
                                </Stack>
                            </Grid>

                            {/* Other Group */}
                            <Grid item xs={12} md={filter.cameraType?.Value === CameraType.PTZ ? 2 : 3}>
                                <Stack spacing={2} bgcolor={color.white} p={2} borderRadius={2}>
                                    <Typography variant="h6">OTHER</Typography>
                                    <StyledDateRangePicker
                                        value={{ startDate: filter.from, endDate: filter.to }}
                                        onChange={(value) => {
                                            setFilter((prev) => ({
                                                ...prev,
                                                from: value.startDate,
                                                to: value.endDate,
                                                page: 1,
                                            }));
                                            handleChangeParams({
                                                from: moment(value.startDate).startOf('d').toISOString(),
                                                to: moment(value.endDate).endOf('d').toISOString(),
                                                cameraType: filter.cameraType?.Id,
                                            });
                                        }}
                                        label={'Production date'}
                                        disableLoading={loadingState.cameras}
                                        initialSettings={
                                            {
                                                // isInvalidDate: (date: Moment) => {
                                                //     return moment(date).isAfter();
                                                // },
                                            }
                                        }
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    }
                />
                <Stack mt={3}>
                    <Typography textAlign={'right'} sx={{ color: color.priText }}>
                        Total: {pagingCameras.total}
                    </Typography>
                </Stack>
            </Box>
            <BaseListRender
                styleContainer={{ mt: 2, mb: 5 }}
                styleList={{ gap: 2 }}
                paging={pagingCameras}
                renderedItem={(camera) => {
                    const cameraTypeValue = camera.Solar4gCamera
                        ? CameraType.Solar
                        : camera.PTZCamera
                          ? CameraType.PTZ
                          : camera.PMC8266Camera
                            ? CameraType.PMC8266
                            : CameraType.SP111;
                    return (
                        <CameraItem
                            cameraItem={camera}
                            cameraType={cameraTypeValue}
                            onChangeStatus={onChangeStatus}
                            onDeleteCamera={onDeleteCamera}
                            onChangeVerification={onChangeVerification}
                        />
                    );
                }}
                isLoading={loadingState.cameras}
                setPage={(page) => {
                    setFilter((prev) => ({ ...prev, page }));
                    handleChangeParams({ page });
                }}
            />
        </Stack>
    );
}

export interface LoadingState {
    location: boolean;
    cameras: boolean;
}
export const ContainerSort = styled(Grid)({
    background: '#FFF',
});

const LabeledContainer = ({ title, children }) => {
    return (
        <Box sx={{ position: 'relative', background: '#fff', border: '1px solid #ddd' }}>
            <label
                className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-formControl MuiInputLabel-animated MuiInputLabel-shrink MuiInputLabel-outlined MuiFormLabel-colorPrimary MuiInputLabel-root MuiInputLabel-formControl MuiInputLabel-animated MuiInputLabel-shrink MuiInputLabel-outlined css-1hu70xq-MuiFormLabel-root-MuiInputLabel-root"
                data-shrink="true"
                htmlFor=":r5s:"
                id=":r5s:-label"
            // style={{ background: '#fff', width: '80px', textAlign: 'center' }}
            >
                Search by
            </label>

            {children}
        </Box>
    );
};
